/**
 * Catálogo de canais e fluxos multi-recurso para Central de Ajuda e assistente da plataforma.
 * Textos visíveis em linguagem simples; searchText mantém sinônimos técnicos para busca/IA.
 */

export const CHANNEL_ROUTING = {
  title: "Roteamento padrão nos canais",
  summary:
    "Ao criar ou editar um canal em Configurações → Canais, a seção Roteamento padrão define para onde vão as novas solicitações. As três opções são mutuamente exclusivas: equipe humana, chatbot (atendimento automático) ou agente de IA. Também é possível definir o Tipo de Atendimento padrão. Quando o canal usa agente de IA, as mensagens recebidas são tratadas pelo agente configurado até haver transferência para humano ou chatbot.",
  url: "/help?section=settings&area=channels",
  searchText:
    "roteamento canal origem id_agente idusuario chatbot ura humano agente ia passar por agente tipo atendimento default",
};

/** Tipos de canal disponíveis para integração. */
export const CHANNEL_TYPES = [
  {
    id: 20,
    key: "whatsapp_gupshup",
    label: "WhatsApp oficial (Meta)",
    category: "whatsapp",
    summary:
      "WhatsApp Business pela conexão oficial da Meta: mensagens, modelos aprovados e fluxos automatizados. Conecte em Configurações → Canais, preencha credenciais Meta/Gupshup e configure roteamento (agente de IA, chatbot ou equipe humana).",
    setup: [
      "Configurações → Canais → adicionar WhatsApp oficial (Meta)",
      "Informe número, credenciais e valide a conexão",
      "Em Roteamento padrão, escolha agente de IA, chatbot ou usuários humanos",
      "Defina Tipo de Atendimento e teste enviando uma mensagem",
    ],
    segments: [],
  },
  {
    id: 6,
    key: "whatsapp_qr",
    label: "WhatsApp (QR Code)",
    category: "whatsapp",
    summary:
      "Conecte escaneando um QR Code no celular (não usa a conexão oficial Meta). Indicado quando a operação usa WhatsApp Web ou sessão dedicada. Roteamento igual aos demais canais: agente de IA, chatbot ou equipe humana.",
    setup: [
      "Configurações → Canais → WhatsApp (QR Code)",
      "Escaneie o QR Code e aguarde conexão",
      "Configure roteamento e tipo de atendimento",
      "Teste com mensagem real",
    ],
    segments: [],
  },
  {
    id: 12,
    key: "facebook_messenger",
    label: "Facebook Messenger",
    category: "social",
    summary:
      "Recebe mensagens da página no Facebook. Conecte a conta Meta, autorize a página e configure para qual agente de IA, chatbot ou equipe humana as conversas devem ir.",
    setup: [
      "Configurações → Canais → Facebook Messenger",
      "Autorize página e permissões Meta",
      "Configure roteamento padrão",
      "Teste enviando mensagem pela página",
    ],
    segments: [],
  },
  {
    id: 21,
    key: "instagram_messenger",
    label: "Instagram Direct",
    category: "social",
    summary:
      "Mensagens diretas do Instagram. Integração Meta; após conectar, use Roteamento padrão para direcionar a agente de IA, chatbot ou equipe humana.",
    setup: [
      "Configurações → Canais → Instagram Direct",
      "Conecte conta comercial Instagram",
      "Configure roteamento e tipo de atendimento",
      "Teste pelo Direct",
    ],
    segments: [],
  },
  {
    id: 23,
    key: "facebook_comments",
    label: "Facebook Comentários",
    category: "social",
    summary:
      "Recebe comentários em posts da Página do Facebook como solicitações. O agente pode responder publicamente no post ou, via gatilhos, enviar Private Reply no Messenger.",
    setup: [
      "Configurações → Canais → Facebook Comentários",
      "Conecte a Página Meta (mesmo OAuth do Messenger)",
      "Configure roteamento para agente de IA ou equipe",
      "Teste comentando em um post da Página",
    ],
    segments: [],
  },
  {
    id: 44,
    key: "instagram_comments",
    label: "Instagram Comentários",
    category: "social",
    summary:
      "Recebe comentários em posts e reels do Instagram como solicitações. O agente pode responder no comentário ou, via gatilhos, abrir DM por Private Reply.",
    setup: [
      "Configurações → Canais → Instagram Comentários",
      "Conecte a conta Instagram Business via Meta",
      "Configure roteamento para agente de IA ou equipe",
      "Teste comentando em um post/reel",
    ],
    segments: [],
  },
  {
    id: 35,
    key: "email_aws_ses",
    label: "E-mail",
    category: "email",
    summary:
      "Envio e recepção de e-mails pela plataforma. Valide domínio, configure endereços e roteamento para agente de IA ou equipe.",
    setup: [
      "Configurações → Canais → E-mail",
      "Valide domínio e endereços de envio/recebimento",
      "Configure roteamento padrão",
      "Envie e receba e-mail de teste",
    ],
    segments: [],
  },
  {
    id: 30,
    key: "smtp",
    label: "E-mail (servidor próprio)",
    category: "email",
    summary:
      "Integração com caixa corporativa (ex.: Microsoft 365). Útil quando a empresa mantém servidor de e-mail próprio.",
    setup: [
      "Configurações → Canais → E-mail (servidor próprio)",
      "Informe dados do servidor e credenciais",
      "Configure roteamento",
      "Teste envio e recebimento",
    ],
    segments: [],
  },
  {
    id: 5,
    key: "telephony_xpro",
    label: "Telefonia Xpro",
    category: "voice",
    summary: "Telefonia integrada da plataforma para ligações e abertura automática de solicitações.",
    setup: ["Configurações → Canais → Telefonia Xpro", "Configure número e roteamento", "Teste uma ligação"],
    segments: [],
  },
  {
    id: 29,
    key: "telephony",
    label: "Telefonia / PABX",
    category: "voice",
    summary: "Integração com central telefônica (PABX) para abrir solicitações ao receber ligações.",
    setup: ["Configurações → Canais → Telefonia", "Configure integração PABX", "Teste ligação entrante"],
    segments: [],
  },
  {
    id: 31,
    key: "sms",
    label: "SMS",
    category: "sms",
    summary: "Envio e recebimento de SMS pela plataforma. Contrate ou reutilize números (ex.: Telefonia Xpro) pela tela de canais.",
    setup: [
      "Configurações → Canais → SMS",
      "Reutilize um número da Telefonia Xpro com SMS ou compre um novo",
      "Salve o canal (envio/recebimento são provisionados automaticamente)",
      "Teste envio e recebimento de SMS",
    ],
    segments: [],
  },
  {
    id: 42,
    key: "rcs_business",
    label: "RCS Business",
    category: "sms",
    summary:
      "Mensagens RCS (texto) pela plataforma. Escolha o serviço de mensagens e salve — envio e recebimento são configurados automaticamente.",
    setup: [
      "Configurações → Canais → RCS Business",
      "Selecione o serviço de mensagens (liberado pela plataforma)",
      "Salve o canal e teste o envio pelo chat",
    ],
    segments: [],
  },
  {
    id: 40,
    key: "ecommerce",
    label: "Ecommerce",
    category: "commerce",
    summary: "Pedidos, carrinho abandonado e eventos do e-commerce (loja online). Eventos podem abrir solicitações com tipos configuráveis.",
    setup: ["Configurações → Canais → Ecommerce", "Configure avisos automáticos e tipos por evento", "Cadastre pagamento, etapas visíveis do funil e teste um pedido"],
    segments: [],
  },
  {
    id: 41,
    key: "website_viacao",
    label: "Website Viação",
    category: "web",
    summary: "Site com formulário ou chat no site para transporte rodoviário. URL temporária ou domínio personalizado.",
    setup: ["Configurações → Canais → Website Viação", "Configure domínio e roteamento"],
    segments: ["passenger_transport"],
  },
  {
    id: 43,
    key: "xpro_cliente",
    label: "Portal do cliente",
    category: "portal",
    summary: "Portal web onde clientes abrem e acompanham chamados. Roteamento define quem atende novas solicitações do portal.",
    setup: ["Configurações → Canais → Portal do cliente", "Configure roteamento e permissões"],
    segments: [],
  },
  {
    id: 46,
    key: "reclame_aqui",
    label: "Reclame Aqui",
    category: "reputation",
    summary:
      "Importa reclamações do Reclame Aqui como solicitações. Escolha monitoramento do perfil público (só entrada) ou API HugMe (entrada e resposta pública no chat). Configure roteamento padrão como nos demais canais.",
    setup: [
      "Configurações → Canais → Reclame Aqui",
      "Escolha o modo: Monitoramento (slug do perfil) ou API HugMe (username/senha da RA API)",
      "Para API: contrate em parasuaempresa.reclameaqui.com.br / developer.hugme.com.br",
      "Configure roteamento (humano, chatbot ou agente de IA) e tipo de atendimento",
      "Aguarde o worker (cerca de 5 min) ou verifique o status do poll no canal",
    ],
    segments: [],
  },
  {
    id: 47,
    key: "site_webchat",
    label: "Chat no site",
    category: "web",
    summary:
      "Botão de conversa no site. O visitante escreve sem sair da página e a solicitação chega no chat do Xpro, com o mesmo roteamento dos outros canais.",
    setup: [
      "Configurações → Canais → Chat no site",
      "Salve o canal para gerar o código",
      "Copie o código e cole antes de </body> no site, ou no rodapé do WordPress com o WPCode",
      "Ajuste cor, lado da tela e a frase do botão, e veja a prévia",
      "Configure roteamento (equipe, chatbot ou agente de IA) e tipo de atendimento",
    ],
    segments: [],
  },
  {
    id: 0,
    key: "default",
    label: "Canal genérico",
    category: "other",
    summary: "Canal sem integração específica — uso interno ou origens personalizadas.",
    setup: ["Configurações → Canais → Canal genérico", "Configure nome e roteamento"],
    segments: [],
  },
];

/** Fluxos que cruzam mais de um recurso da plataforma. */
export const PLATFORM_WORKFLOWS = [
  {
    id: "workflow.whatsapp_ai_agent",
    title: "Atendimento no WhatsApp com agente de IA",
    summary:
      "Crie um agente de IA, conecte WhatsApp e roteie o canal para o agente. Novas mensagens no WhatsApp serão respondidas pelo agente até transferência para humano ou chatbot.",
    steps: [
      "Configurações → Agentes de IA: crie o agente, escolha função (FAQ, recepcionista etc.) e anexe documentos/orientações.",
      "Configure tom, horário comercial e regras de ciclo de vida se necessário (transferência para humano).",
      "Configurações → Canais: adicione WhatsApp oficial (Meta) ou WhatsApp (QR Code) e conclua a conexão.",
      "Na seção Roteamento padrão do canal, em Passar por agente de IA, selecione o agente criado.",
      "Defina Tipo de Atendimento (obrigatório) e salve o canal.",
      "Envie mensagem de teste pelo WhatsApp e confira a resposta do agente em Atendimento.",
    ],
    url: "/help?section=settings&area=channels",
    searchText:
      "whatsapp agente ia atendimento criar canal roteamento passar por agente empresa mensagem automatico cloud api skill",
  },
  {
    id: "workflow.channel_chatbot_then_human",
    title: "WhatsApp com chatbot e escalonamento para humano",
    summary:
      "Canal roteado para chatbot com triagem automática; depois transferência para atendente conforme fluxo ou regra.",
    steps: [
      "Configurações → Chatbots: monte o fluxo de triagem.",
      "Configurações → Canais → WhatsApp: em Roteamento padrão escolha o chatbot (não agente de IA).",
      "No chatbot, use ação Alterar solicitação para transferir a humano quando necessário.",
      "Teste o fluxo completo.",
    ],
    url: "/help?section=settings&area=chatbot",
    searchText: "whatsapp chatbot ura triagem humano transferir canal roteamento",
  },
  {
    id: "workflow.channel_ai_agent_instagram",
    title: "Atendimento no Instagram com agente de IA",
    summary: "Mesmo fluxo do WhatsApp: agente de IA + canal Instagram Direct com roteamento para o agente.",
    steps: [
      "Crie e configure o agente de IA em Configurações → Agentes de IA.",
      "Configurações → Canais → Instagram Direct: conecte conta Meta.",
      "Roteamento padrão → Passar por agente de IA → selecione o agente.",
      "Teste pelo Direct do Instagram.",
    ],
    url: "/help?section=settings&area=channels",
    searchText: "instagram direct agente ia atendimento canal roteamento meta",
  },
  {
    id: "workflow.channel_ai_agent_email",
    title: "E-mail com agente de IA",
    summary: "Canal de e-mail com respostas automáticas via agente de IA.",
    steps: [
      "Crie agente de IA com materiais adequados a e-mail (FAQ, políticas).",
      "Configurações → Canais → E-mail: valide domínio.",
      "Roteamento padrão → agente de IA.",
      "Envie e-mail de teste.",
    ],
    url: "/help?section=settings&area=channels",
    searchText: "email e-mail agente ia canal roteamento atendimento",
  },
  {
    id: "workflow.agent_transfer_to_chatbot",
    title: "Transferir de agente de IA para chatbot",
    summary: "Configure ciclo de vida do agente para enviar conversa a um chatbot.",
    steps: [
      "Configurações → Agentes de IA → agente → função do agente → Ciclo de vida.",
      "Ative fase Escalonamento ou Falha.",
      "Transferir para → Chatbot → selecione chatbot.",
      "Salve e teste.",
    ],
    url: "/help?section=settings&area=agents",
    searchText: "transferir agente ia chatbot ura escalonamento ciclo vida skill",
  },
];

/** Mapa id → label para uso em snapshot/API. */
export const CHANNEL_TYPE_LABEL_BY_ID = Object.fromEntries(
  CHANNEL_TYPES.map((c) => [String(c.id), c.label])
);
